import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactPage() {
  const [formState, setFormState] = useState<FormState>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setFormState('submitting');
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d6vvtmlt4s7j0tv5ikj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setFormState('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormState('error');
      }
    } catch {
      setFormState('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Page Hero Banner */}
      <section className="relative h-72 flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=professional%20business%20meeting%20between%20medical%20consultants%20and%20healthcare%20executives%20in%20modern%20conference%20room%2C%20regenerative%20medicine%20industry%20discussion%2C%20clean%20corporate%20environment%20with%20warm%20lighting&width=1440&height=576&seq=contact-hero-01&orientation=landscape"
          alt="Contact Us"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/70 via-blue-900/60 to-blue-950/70" />
        <div className="relative z-10 text-center px-6">
          <span className="inline-block bg-red-600 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            OnPoint USA
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Contact Us
          </h1>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-12">

          {/* Left Info Panel */}
          <div className="md:col-span-2">
            <span className="text-red-600 text-xs font-bold uppercase tracking-widest">Reach Out</span>
            <h2 className="text-2xl font-bold mt-2 mb-3" style={{ color: '#002868', fontFamily: 'Montserrat, sans-serif' }}>
              Our Commitment to Service
            </h2>
            <div className="w-10 h-0.5 bg-red-600 mb-6" />
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              Your inquiries matter to us — real answers, real people, real fast.
            </p>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              We do our best to respond to all inquiries within 24 hours.
            </p>
            <p className="text-gray-600 text-sm leading-relaxed mb-10">
              You&apos;ll always receive a response directly from an OnPoint USA representative — we never use bots or outsource our communications.
            </p>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: '#002868' }}>
                  <i className="ri-map-pin-2-fill text-white" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1">Address</p>
                  <p className="text-gray-700 text-sm font-medium leading-relaxed">
                    1414 Shore District Dr Ste 3101<br />Austin, TX 78741
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: '#002868' }}>
                  <i className="ri-mail-fill text-white" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1">Email</p>
                  <a
                    href="mailto:gabriel@onpointlabservices.com"
                    className="text-red-600 text-sm font-medium hover:text-red-700 transition-colors"
                  >
                    gabriel@onpointlabservices.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: '#002868' }}>
                  <i className="ri-time-fill text-white" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1">Response Time</p>
                  <p className="text-gray-700 text-sm font-medium">Within 24 hours</p>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="mt-10 p-5 rounded-lg border border-blue-100" style={{ background: '#f8f9ff' }}>
              <p className="text-xs font-bold uppercase tracking-wide mb-4" style={{ color: '#002868' }}>Why OnPoint USA?</p>
              <ul className="space-y-2">
                {[
                  '10+ Years Regenerative Medicine Expertise',
                  'Extensive Industry Network & Connections',
                  'U.S.-Based, Direct Communication',
                  'Dedicated Account Representative',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-gray-600">
                    <i className="ri-check-double-line text-red-600 text-sm" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right Form Panel */}
          <div className="md:col-span-3">
            <div className="rounded-xl p-8 border border-gray-100" style={{ background: '#f8f9ff' }}>
              <h3 className="text-xl font-bold mb-2" style={{ color: '#002868', fontFamily: 'Montserrat, sans-serif' }}>
                Send Us A Message
              </h3>
              <p className="text-gray-500 text-sm mb-6">Fill out the form below and we&apos;ll get back to you promptly.</p>

              {formState === 'success' ? (
                <div className="py-12 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-4" style={{ background: '#002868' }}>
                    <i className="ri-check-double-line text-white text-2xl" />
                  </div>
                  <h4 className="font-bold text-lg mb-2" style={{ color: '#002868' }}>Message Sent!</h4>
                  <p className="text-gray-500 text-sm">Thanks for reaching out. We&apos;ll be in touch within 24 hours.</p>
                  <button
                    onClick={() => setFormState('idle')}
                    className="mt-6 text-sm text-red-600 font-semibold hover:text-red-700 cursor-pointer"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">First Name *</label>
                      <input
                        type="text"
                        name="first_name"
                        required
                        placeholder="John"
                        className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-red-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Last Name *</label>
                      <input
                        type="text"
                        name="last_name"
                        required
                        placeholder="Doe"
                        className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-red-400 transition-colors"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="(555) 000-0000"
                        className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-red-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="john@example.com"
                        className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-red-400 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Organization / Practice</label>
                    <input
                      type="text"
                      name="organization"
                      placeholder="Your clinic or company name"
                      className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-red-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Message * <span className={`ml-auto float-right ${charCount > 450 ? 'text-red-500' : 'text-gray-400'}`}>{charCount}/500</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="Tell us how we can help you..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full bg-white border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-red-400 transition-colors resize-none"
                    />
                  </div>
                  {formState === 'error' && (
                    <p className="text-red-500 text-xs">Something went wrong. Please try again or email us directly.</p>
                  )}
                  <button
                    type="submit"
                    disabled={formState === 'submitting'}
                    className="w-full text-white font-bold text-sm py-3.5 rounded-md hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap disabled:opacity-60"
                    style={{ background: '#dc2626' }}
                  >
                    {formState === 'submitting' ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
